/*
  Detailed Report Generator
  ------------------------------------------------------------
  Produces a structured JSON object and a Markdown report summarizing
  AI agent opportunity ranges and likely implemented agents based on
  current user inputs and industry data. Reuses shared math utilities.
*/

import { industryData } from '../data/industryDataComplete';
import { generalAgents, augmentAgentMetrics } from '../data/industryTasks';
import {
  TIME_SAVED_CAP,
  REVENUE_IMPACT_CAP,
  HOURS_PER_FTE,
  computeAutomationFactor,
  computeIndustryWastePercent,
  computeAdminWasteFromProcess,
  computeAIAgentsAverage,
  computePotentialSavings,
  classifyAgentsByMaturity,
  proportionalAllocate,
  safeDiv,
} from './roiMath';

/**
 * Generate a detailed report for the current inputs.
 *
 * @param {Object} params
 * @param {string} params.industryId
 * @param {number} params.employeeCount
 * @param {number} params.annualRevenue
 * @param {number} params.automationMaturity // 1..10
 * @param {number} params.processMaturity    // 1..10
 * @param {number} params.hourlyCost         // average FTE hourly cost (e.g., 50)
 * @param {boolean} [params.includeGeneralAgents=true]
 * @param {number} [params.topN=10]
 * @returns {null | {
 *   meta: any,
 *   totals: any,
 *   opportunities: { topN: any[], remaining: any[] },
 *   implemented: { likelyAchieved: any[] },
 *   assumptions: any,
 *   markdown: string
 * }}
 */
export function generateDetailedReport(params) {
  const {
    industryId,
    employeeCount,
    annualRevenue,
    automationMaturity,
    processMaturity,
    hourlyCost,
    includeGeneralAgents = true,
    topN = 10,
  } = params || {};

  const industry = industryData.find(i => i.id === industryId);
  if (!industry) return null;

  // Prepare agent universe: industry + general
  const industryAgentsRaw = Array.isArray(industry.agents) ? industry.agents : [];
  const industryAgents = industryAgentsRaw.map(augmentAgentMetrics);
  const generalAgentsAug = includeGeneralAgents ? generalAgents : [];
  const allAgents = [...industryAgents, ...generalAgentsAug];

  const automationFactor = computeAutomationFactor(automationMaturity);
  const industryWastePercent = computeIndustryWastePercent(industry, automationMaturity);
  const { low: adminWasteLow, high: adminWasteHigh } = computeAdminWasteFromProcess(processMaturity);
  const aiAgentsAverage = computeAIAgentsAverage(industry, annualRevenue);
  const potentialSavings = computePotentialSavings(aiAgentsAverage, automationMaturity, processMaturity);

  // Classify by maturity threshold T = 11 - automationMaturity
  const { threshold: maturityThreshold, implemented, opportunities } = classifyAgentsByMaturity(
    automationMaturity,
    allAgents
  );

  // Build weights for opportunities: combine labor and revenue components
  const laborWeights = opportunities.map(a => Math.max(0, a.timeSavedFTE || 0));
  const revenueWeights = opportunities.map(a => Math.max(0, a.revenuePotential || 0));

  const laborSum = laborWeights.reduce((s, v) => s + v, 0);
  const revenueSum = revenueWeights.reduce((s, v) => s + v, 0);

  // Combine normalized labor and revenue weights and renormalize
  const combinedWeightsRaw = opportunities.map((_, idx) => {
    const lw = laborSum > 0 ? laborWeights[idx] / laborSum : 0;
    const rw = revenueSum > 0 ? revenueWeights[idx] / revenueSum : 0;
    return lw + rw;
  });
  const allocatedRanges = proportionalAllocate(potentialSavings.low, potentialSavings.high, combinedWeightsRaw);

  // Contextual (non-headline) labor: estimate using hourly cost
  // We first compute capped & scaled total time saved across opportunities only
  const timeSavedSum = laborWeights.reduce((s, v) => s + v, 0);
  const timeSavedAdj = Math.min(timeSavedSum, TIME_SAVED_CAP);

  // For a proportional share of time-saved impact per opportunity agent
  const timeShares = (timeSavedSum > 0)
    ? laborWeights.map(v => v / timeSavedSum)
    : laborWeights.map(_ => 0);

  const fteSavedTotal = employeeCount * timeSavedAdj * automationFactor;

  const opportunityItems = opportunities.map((a, idx) => {
    const range = allocatedRanges[idx] || { low: 0, high: 0 };
    const timeShare = timeShares[idx] || 0;
    const fteSavedAgent = fteSavedTotal * timeShare;
    const hoursSavedAgent = fteSavedAgent * HOURS_PER_FTE;
    const laborDollarsContext = hoursSavedAgent * (hourlyCost || 0);

    // Percent of headline
    const pctLow = safeDiv(range.low, potentialSavings.low);
    const pctHigh = safeDiv(range.high, potentialSavings.high);

    return {
      label: a.label,
      type: industryAgents.includes(a) ? 'industry' : 'general',
      score: a.score,
      timeSavedFTE: a.timeSavedFTE,
      revenuePotential: a.revenuePotential,
      jobSatisfaction: a.jobSatisfaction,
      dollarsLow: range.low,
      dollarsHigh: range.high,
      percentOfHeadlineLow: pctLow,
      percentOfHeadlineHigh: pctHigh,
      fteSaved: fteSavedAgent,
      hoursSaved: hoursSavedAgent,
      laborDollarsContext,
    };
  });

  // Sort opportunities by dollarsHigh desc, then by revenuePotential desc
  opportunityItems.sort((a, b) => (b.dollarsHigh - a.dollarsHigh) || (b.revenuePotential - a.revenuePotential));

  const topItems = opportunityItems.slice(0, Math.max(0, topN));
  const remainingItems = opportunityItems.slice(Math.max(0, topN));

  // Likely implemented (congrats) section, using proportional allocation over the same headline range for context only
  const implLaborWeights = implemented.map(a => Math.max(0, a.timeSavedFTE || 0));
  const implRevenueWeights = implemented.map(a => Math.max(0, a.revenuePotential || 0));
  const implLaborSum = implLaborWeights.reduce((s, v) => s + v, 0);
  const implRevenueSum = implRevenueWeights.reduce((s, v) => s + v, 0);
  const implCombinedWeights = implemented.map((_, idx) => {
    const lw = implLaborSum > 0 ? implLaborWeights[idx] / implLaborSum : 0;
    const rw = implRevenueSum > 0 ? implRevenueWeights[idx] / implRevenueSum : 0;
    return lw + rw;
  });
  const implRanges = proportionalAllocate(potentialSavings.low, potentialSavings.high, implCombinedWeights);

  // Contextual labor for implemented
  const implTimeSavedSum = implLaborWeights.reduce((s, v) => s + v, 0);
  const implTimeSavedAdj = Math.min(implTimeSavedSum, TIME_SAVED_CAP);
  const implTimeShares = (implTimeSavedSum > 0)
    ? implLaborWeights.map(v => v / implTimeSavedSum)
    : implLaborWeights.map(_ => 0);
  const implFteSavedTotal = employeeCount * implTimeSavedAdj * automationFactor;

  const implementedItems = implemented.map((a, idx) => {
    const range = implRanges[idx] || { low: 0, high: 0 };
    const timeShare = implTimeShares[idx] || 0;
    const fteSavedAgent = implFteSavedTotal * timeShare;
    const hoursSavedAgent = fteSavedAgent * HOURS_PER_FTE;
    const laborDollarsContext = hoursSavedAgent * (hourlyCost || 0);

    return {
      label: a.label,
      type: industryAgents.includes(a) ? 'industry' : 'general',
      score: a.score,
      timeSavedFTE: a.timeSavedFTE,
      revenuePotential: a.revenuePotential,
      jobSatisfaction: a.jobSatisfaction,
      dollarsLow: range.low,
      dollarsHigh: range.high,
      fteSaved: fteSavedAgent,
      hoursSaved: hoursSavedAgent,
      laborDollarsContext,
    };
  });

  // Totals (opportunities only) – by construction equal to headline low/high
  const totals = {
    potentialSavings,
    aiAgentsAverage,
    fteSavedTotal, // opportunities side only (context)
    automationFactor,
    industryWastePercent,
    adminWasteLow,
    adminWasteHigh,
  };

  // Compose Markdown
  const fmtMoney = (x) => `$${Math.round(x).toLocaleString('en-US')}`;
  // eslint-disable-next-line no-unused-vars
  const fmtPct = (x) => `${Math.round((x || 0) * 100)}%`;

  const mdLines = [];
  mdLines.push(`# Detailed AI Automation Report`);
  mdLines.push('');
  mdLines.push(`**Industry:** ${industry.name}`);
  mdLines.push(`**Annual Revenue:** ${fmtMoney(annualRevenue)}  `);
  mdLines.push(`**Employees:** ${employeeCount.toLocaleString('en-US')}  `);
  mdLines.push(`**Average FTE Hourly Cost:** ${fmtMoney(hourlyCost)} / hour`);
  mdLines.push('');
  mdLines.push(`## Headline Opportunity Range`);
  mdLines.push(`- Low: ${fmtMoney(potentialSavings.low)}  `);
  mdLines.push(`- High: ${fmtMoney(potentialSavings.high)}`);
  mdLines.push(`- Based on industry waste × admin waste and your maturity levels`);
  mdLines.push('');

  if (topItems.length > 0) {
    mdLines.push(`## Top ${topItems.length} Opportunities`);
    topItems.forEach((it, i) => {
      mdLines.push(`- ${i + 1}. ${it.label} — ${fmtMoney(it.dollarsLow)} to ${fmtMoney(it.dollarsHigh)}`);
    });
    mdLines.push('');
  }

  if (remainingItems.length > 0) {
    mdLines.push(`## Remaining Opportunities`);
    remainingItems.forEach((it) => {
      mdLines.push(`- ${it.label} — ${fmtMoney(it.dollarsLow)} to ${fmtMoney(it.dollarsHigh)}`);
    });
    mdLines.push('');
  }

  if (implementedItems.length > 0) {
    mdLines.push(`## Congratulations — Likely Implemented Already`);
    mdLines.push(`These items are not added to the opportunity totals but are shown as likely achieved results based on your current maturity level.`);
    implementedItems.forEach((it) => {
      mdLines.push(`- ${it.label} — ${fmtMoney(it.dollarsLow)} to ${fmtMoney(it.dollarsHigh)} (context)`);
    });
    mdLines.push('');
  }

  mdLines.push(`## Assumptions & Notes`);
  mdLines.push(`- Classification uses threshold T = clamp(automationMaturity - 1, 1..10). Items with score ≤ T are treated as already implemented.  `);
  mdLines.push(`- For this report: automationMaturity = ${automationMaturity}, so T = ${maturityThreshold}. Implemented items: ${implementedItems.length}, Opportunity items: ${topItems.length + remainingItems.length}.  `);
  mdLines.push(`- Caps: time-saved ≤ ${Math.round(TIME_SAVED_CAP * 100)}%, revenue impact ≤ ${Math.round(REVENUE_IMPACT_CAP * 100)}%  `);
  mdLines.push(`- Automation factor applied: ${automationFactor.toFixed(2)}  `);
  mdLines.push(`- Per-agent amounts are ranges aligned to the headline opportunity and allocated proportionally by labor and revenue weights  `);
  mdLines.push(`- Separate labor context uses your hourly cost and estimated hours saved; it does not affect headline totals  `);

  const markdown = mdLines.join('\n');

  return {
    meta: {
      industryId,
      industryName: industry.name,
      employeeCount,
      annualRevenue,
      hourlyCost,
      automationMaturity,
      processMaturity,
      maturityThreshold,
      includedCounts: {
        industryAgents: industryAgents.length,
        generalAgents: generalAgentsAug.length,
      },
    },
    totals,
    opportunities: { topN: topItems, remaining: remainingItems },
    implemented: { likelyAchieved: implementedItems },
    assumptions: {
      allocationMethod: 'proportional-labor+revenue',
      hoursPerFTE: HOURS_PER_FTE,
      caps: { TIME_SAVED_CAP, REVENUE_IMPACT_CAP },
    },
    markdown,
  };
}
